import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Card as DesignCard } from '@hivebrite/design-system/Card';
import { Image } from '@hivebrite/design-system/Image';
import { Link } from '@hivebrite/design-system/Link';
import { useBreakpoint } from '@hivebrite/design-system/Theme';

import { MEMBERSHIPS_PERIODICITIES } from 'hb-react/shared/apps/Memberships/constants/membership';
import { translations } from 'hb-react/shared/apps/Memberships/utils/translations';
import { Price } from 'hb-react/shared/components/Price';
import ReadMore from 'hb-react/shared/components/ReadMore';
import { PARENT_TYPES } from 'hb-react/shared/constants/parentTypes';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import PaymentProviderIcon from 'hb-react/shared/icons/Payments/PaymentProvider';

import { FreeTrailPeriodBadge } from '../FreeTrialPeriodBadge';

const clamp = {
  m: 4,
  s: 5,
};

const getPadding = ({ image, brandBarColor }) => {
  if (image && !brandBarColor) {
    return 3;
  }

  if (!image && !brandBarColor) {
    return 4;
  }

  if (!image && brandBarColor) {
    return 1;
  }

  return 0;
};

const GroupLink = ({ meta, withGroupName = false }) => {
  const isGroupMembership = meta?.parent?.type === PARENT_TYPES.TOPIC;

  if (!isGroupMembership || !withGroupName) {
    return null;
  }

  const { parent } = meta;

  return (
    <Box font="inherit" mb={2}>
      <StyledLink to={parent.landing_page} title={parent.name}>
        {parent.name}
      </StyledLink>
    </Box>
  );
};

const MembershipHeader = ({
  meta,
  subheader,
  title,
  withGroupName,
  image,
  description,
  breakpoint,
}) => (
  <Header>
    <Title>
      <GroupLink meta={meta} withGroupName={withGroupName} />
      <Box font="inherit">{title}</Box>
    </Title>
    {subheader}
    {description && (
      <Content $withImage={!!image}>
        <ReadMore text={description || ''} clamp={clamp[breakpoint]} />
      </Content>
    )}
  </Header>
);

const WrapperWithImage = ({ image, brandBarColor, children }) => (
  <Box pl={getPadding({ image, brandBarColor })}>
    {image && (
      <Box
        alignItems="flex-start"
        width={{ _: '24px', l: '30px' }}
        mr={{ _: 3, l: 4 }}
        flexGrow={0}
        flexShrink={0}
      >
        <ImageWrapper>
          <Image
            src={image}
            alt={s__('Memberships|Membership picture')}
            width="100%"
          />
        </ImageWrapper>
      </Box>
    )}
    {children}
  </Box>
);

const PurchaseAndPayment = ({
  validity,
  price,
  currency,
  periodicity,
  paymentOptions = [],
  paymentMethod,
  hasFreeTrial = false,
  freeTrialPeriodPeriodicity,
}) => (
  <Box flexDirection="column">
    {hasFreeTrial && <FreeTrailPeriodBadge mb={2} withText />}
    <PurchaseOptions
      validity={validity}
      price={price}
      currency={currency}
      periodicity={periodicity}
      paymentOptions={paymentOptions}
      hasFreeTrial={hasFreeTrial}
      freeTrialPeriodPeriodicity={freeTrialPeriodPeriodicity}
    />
    {paymentMethod && (
      <DataRow column={s__('Memberships|Payment method:')}>
        <PaymentMethod {...paymentMethod} />
      </DataRow>
    )}
  </Box>
);

const MembershipCard = ({
  title,
  description,
  price,
  currency,
  validity,
  periodicity,
  payment_method: paymentMethod,
  image,
  logo,
  brandBarColor,
  subheader,
  children,
  meta,
  payment_options: paymentOptions,
  withGroupName = false,
  has_free_trial: membershipHasFreeTrial = false,
  free_trial_period_periodicity: freeTrialPeriodPeriodicity,
}) => {
  const breakpoint = useBreakpoint();
  const { flagEvGsMembershipLogo } = useFeatureFlag();

  const hasFreeTrial =
    membershipHasFreeTrial || paymentOptions?.some((po) => po.has_free_trial);

  const membershipTypeLogo =
    flagEvGsMembershipLogo && logo ? logo.thumbnail_url : image;

  if (breakpoint === 's') {
    return (
      <WrapperWithImage
        image={membershipTypeLogo}
        brandBarColor={brandBarColor}
      >
        <Box flexDirection="column" flexGrow={1} minWidth={0}>
          <MembershipHeader
            title={title}
            subheader={subheader}
            meta={meta}
            withGroupName={withGroupName}
            description={description}
          />
          <CardContainer>
            <Box flexDirection="column">
              <PurchaseAndPayment
                validity={validity}
                price={price}
                currency={currency}
                periodicity={periodicity}
                paymentOptions={paymentOptions}
                paymentMethod={paymentMethod}
                hasFreeTrial={hasFreeTrial}
                freeTrialPeriodPeriodicity={freeTrialPeriodPeriodicity}
              />
              {children && (
                <Box ml={0} width="auto">
                  {children}
                </Box>
              )}
            </Box>
          </CardContainer>
        </Box>
      </WrapperWithImage>
    );
  }

  if (breakpoint === 'm') {
    return (
      <WrapperWithImage
        image={membershipTypeLogo}
        brandBarColor={brandBarColor}
      >
        <WrapperContent $hasChildren={!!children}>
          <MembershipHeader
            title={title}
            subheader={subheader}
            meta={meta}
            withGroupName={withGroupName}
            description={description}
          />
          <Box flexDirection="column" flexGrow={1} minWidth={0}>
            <PurchaseAndPayment
              validity={validity}
              price={price}
              currency={currency}
              periodicity={periodicity}
              paymentOptions={paymentOptions}
              paymentMethod={paymentMethod}
              hasFreeTrial={hasFreeTrial}
              freeTrialPeriodPeriodicity={freeTrialPeriodPeriodicity}
            />
            <Box ml={0} width="auto">
              {children}
            </Box>
          </Box>
        </WrapperContent>
      </WrapperWithImage>
    );
  }

  return (
    <WrapperWithImage image={membershipTypeLogo} brandBarColor={brandBarColor}>
      <Box flexDirection="column" width="100%">
        <Box flexDirection="row" width="100%">
          <MembershipHeader
            title={title}
            subheader={subheader}
            meta={meta}
            withGroupName={withGroupName}
            description={description}
            image={membershipTypeLogo}
            breakpoint={breakpoint}
          />
          <CardContainer>
            <PurchaseAndPayment
              validity={validity}
              price={price}
              currency={currency}
              periodicity={periodicity}
              paymentOptions={paymentOptions}
              paymentMethod={paymentMethod}
              hasFreeTrial={hasFreeTrial}
              freeTrialPeriodPeriodicity={freeTrialPeriodPeriodicity}
            />
          </CardContainer>
        </Box>
        {children && <Box width="auto">{children}</Box>}
      </Box>
    </WrapperWithImage>
  );
};

const PurchaseOption = ({
  price,
  currency,
  periodicity,
  has_free_trial: hasFreeTrial = false,
  free_trial_period_periodicity: freeTrialPeriodPeriodicity,
}) => {
  const isLifelong =
    periodicity && periodicity === MEMBERSHIPS_PERIODICITIES.LIFELONG;

  // eslint-disable-next-line eqeqeq
  if (price == null) {
    return null;
  }

  return (
    <Box flexDirection="column" maxWidth="200px" display="list-item" ml={5}>
      {hasFreeTrial && (
        <Box mb={1}>
          <PurchaseOptionsText minWidth={0}>
            <TextEllipsis>
              {`${s__(
                'Memberships|Free',
              )} / ${freeTrialPeriodPeriodicity} ${s__('Memberships|days')}`}
            </TextEllipsis>
          </PurchaseOptionsText>
        </Box>
      )}
      <Box flexDirection="row" mb={1}>
        <PurchaseOptionsText mr={isLifelong ? 1 : 0}>
          <Price
            price={price}
            currency={currency}
            hasFreeTrial={hasFreeTrial}
          />
        </PurchaseOptionsText>
        <PurchaseOptionsText minWidth={0}>
          <TextEllipsis>
            {isLifelong ? (
              `${s__('Memberships|for lifelong')}`
            ) : (
              <>&nbsp;{`/ ${translations.periodicity[periodicity]}`}</>
            )}
          </TextEllipsis>
        </PurchaseOptionsText>
      </Box>
    </Box>
  );
};

const PurchaseOptions = ({
  price,
  currency,
  periodicity,
  hasFreeTrial,
  freeTrialPeriodPeriodicity,
  paymentOptions = [],
}) => {
  if (paymentOptions.length > 0) {
    return (
      <Box flexDirection="column">
        <Box font="h4-semibold" mb={2}>
          {s__('Memberships|Payment options:')}
        </Box>
        {paymentOptions.map((props) => (
          <Box key={props.id} mb={1}>
            <PurchaseOption {...props} />
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <PurchaseOption
      price={price}
      currency={currency}
      periodicity={periodicity}
      has_free_trial={hasFreeTrial}
      free_trial_period_periodicity={freeTrialPeriodPeriodicity}
    />
  );
};

const PaymentMethod = ({ expiration = {}, provider }) => (
  <Box alignItems="center">
    {provider && (
      <Box mr={1}>
        <PaymentProviderIcon provider={provider} />
      </Box>
    )}
    {expiration.month && expiration.year && (
      <Box>{`${expiration.month}/${expiration.year}`}</Box>
    )}
  </Box>
);

const DataRow = ({ column, children, mt = 2 }) => (
  <Box mt={mt} alignItems="center" mr={5} width="max-content">
    <Box mr={1} fontWeight={1}>
      {column}
    </Box>
    <Box>{children}</Box>
  </Box>
);

MembershipCard.propTypes = {
  description: PropTypes.string,
  price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  currency: PropTypes.shape({
    code: PropTypes.string.isRequired,
    symbol: PropTypes.string.isRequired,
  }).isRequired,
  validity: PropTypes.string,
  periodicity: PropTypes.string.isRequired,
  payment_method: PropTypes.shape({
    provider: PropTypes.string.isRequired,
    expiration: PropTypes.shape({
      month: PropTypes.string.isRequired,
      year: PropTypes.string.isRequired,
    }).isRequired,
  }),
  subheader: PropTypes.node,
};

export default MembershipCard;

export const StyledCard = styled(DesignCard)
  .withConfig({
    shouldForwardProp: (prop) =>
      !['brandBarColor', 'selectable'].includes(prop),
  })
  .attrs(() => ({
    width: '100%',
    pl: { _: 3, l: 4 },
    pt: { _: 4 },
    pb: { _: 8 },
    pr: { _: 6, l: 48 },
  }))`

  ${({ brandBarColor }) =>
    brandBarColor && `border-left: 12px solid ${brandBarColor}`};

  transition: .2s opacity;
  ${({ disabled = false }) =>
    disabled &&
    `
    opacity: 0.6;
    user-select: none;
  `};
`;

const Header = styled(Box).attrs(() => ({
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignSelf: 'flex-start',
  mb: 2,
  width: { s: '100%', l: 'calc(100% - 136px)' },
}))``;

const Title = styled(Box).attrs(() => ({
  font: 'h3-semibold',
  flexDirection: 'column',
  flexWrap: 'nowrap',
  minHeight: { _: '24px', l: '30px' },
  justifyContent: 'center',
}))``;

const Content = styled(Box).attrs(({ $withImage = false }) => ({
  width: '100%',
  maxWidth: {
    l: `calc(100% - ${$withImage ? 16 : 22}px)`,
  },
  flexDirection: 'column',
  mr: { _: 3, l: 6 },
  mt: 2,
  mb: 0,
  textAlign: 'left',
}))``;

const CardContainer = styled(Box).attrs(() => ({
  flexWrap: { _: 'wrap', l: 'no-wrap' },
  flexDirection: { _: 'column', m: 'row' },
  width: { l: '136px' },
  ml: { l: 2 },
  mt: 1,
}))``;

const ImageWrapper = styled(Box).attrs(() => ({
  width: { _: '24px', l: '30px' },
  height: { _: '24px', l: '30px' },
}))`
  border-radius: 50%;
  overflow: hidden;
`;

const PurchaseOptionsText = styled(Box).attrs(() => ({
  font: 'body-regular-asphalt',
}))``;

const TextEllipsis = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const StyledLink = styled(Link)`
  font: inherit;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const WrapperContent = styled(Box).attrs(({ $hasChildren }) => ({
  flexWrap: 'wrap',
  flexGrow: 1,
  minWidth: 0,
  pr: !$hasChildren && 32,
}))``;
